<template>
  <div class="h-full w-full max-w-full rounded-lg border border-slate-300 overflow-hidden">
    <FlexCol class="w-full h-full max-w-full relative table-outer bg-white text-black">
      <div class="grow overflow-auto">
        <table data-testid="tablefull" class="grow" :class="{ 'table-fixed': hasCustomWidths }" :style="tableStyle">
          <thead class="header top-0 h-10 uppercase">
            <tr v-if="localHeaders.length" class="bg-slate-200 border-b border-slate-300">
              <th
                v-for="(header, i) in localHeaders"
                :key="header.field ?? i"
                :draggable="canMoveHeader(header)"
                :data-tablefull-header="header.field"
                :data-tablefull-order-display-comparable="header.field === (header.orderField ?? header.field) && !header.raw && !header.format"
                @dragstart="handleHeaderDragStart(i, $event)"
                @dragover.prevent="handleHeaderDragOver(i, $event)"
                @dragleave="handleHeaderDragLeave(i)"
                @drop.prevent="handleHeaderDrop(i)"
                @dragend="handleHeaderDragEnd"
                class="text-nowrap select-none border-r border-slate-300 relative overflow-hidden"
                :class="[
                  cellSpacing,
                  canMoveHeader(header) ? 'cursor-move' : 'cursor-default',
                  draggedHeaderIndex === i ? 'bg-primary-100' : '',
                  dragOverHeaderIndex === i ? 'bg-primary-200 ring-1 ring-primary-400' : '',
                  i === localHeaders.length - 1 && stickyActions ? 'sticky right-0 bg-slate-200 z-[3]' : '',
                ]"
                :style="getColumnStyle(header)">
                <Flex gap-3 items-center class="min-w-0 overflow-hidden" :class="[header.headerClass]">
                  <slot :name="`td-${header.field}`" :item="header">
                    {{ header.title }}

                    <div v-if="!header.disableOrder" :data-tablefull-sort-status="getStatus(header)">
                      <i :data-tablefull-sort="header.field" class="fa-solid fa-sort-down cursor-pointer ml-2" @click="setOrder(header, 2, header.table)" v-if="getStatus(header) == 1" />
                      <i :data-tablefull-sort="header.field" class="fa-solid fa-sort-up cursor-pointer ml-2" @click="setOrder(header, 0, header.table)" v-else-if="getStatus(header) == 2" />
                      <i :data-tablefull-sort="header.field" class="fa-solid fa-sort cursor-pointer ml-2" @click="setOrder(header, 1, header.table)" v-else />
                    </div>
                  </slot>
                </Flex>
                <div v-if="canResizeHeader(header)" class="absolute top-0 right-0 h-full w-2 cursor-col-resize select-none z-[4] hover:bg-primary-200/60" @mousedown.prevent.stop="startResize($event, i)" @dragstart.prevent />
              </th>
              <th v-if="expanded" class="w-10 min-w-10 max-w-10 border-r border-slate-300 text-center"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(item, index) in processedItems" :key="getRowKey(item, index)">
              <tr
                data-tablefull-row
                @click="sendLeftClick(item)"
                @click.right.prevent="sendRightClick(item)"
                @dblclick="sendDoubleClick(item)"
                class="cursor-pointer group"
                :class="[
                  computedClass(item),
                  { 'border-b border-slate-300': !expanded || !item.expanded },
                  { '!bg-blue-500 !text-white': compareItem(item) },
                  { 'hover:bg-blue-100': !compareItem(item) },
                  { 'text-red-400': item.errorLine },
                  { 'divide-x': cellBorder },
                ]">
                <td
                  v-for="(header, hIndex) in localHeaders"
                  :key="header.field ?? hIndex"
                  :data-tablefull-cell="header.field"
                  :class="[header.itemClass, 'text-nowrap overflow-hidden', cellSpacing, hIndex === localHeaders.length - 1 && stickyActions ? 'sticky right-0' : '']"
                  :style="getColumnStyle(header)">
                  <div v-if="item" class="h-full min-w-0 overflow-hidden" :class="[header.field === 'actions' ? 'flex justify-center' : '']">
                    <slot :name="header.field" :item="item" :rowIndex="index">
                      {{ getItemValue(item, header) }}
                    </slot>
                  </div>
                </td>

                <td v-if="expanded && mapNotExpandedItems(item)" class="w-10 min-w-10 max-w-10 px-2 text-center">
                  <IconSolid :icon="item.expanded ? 'fa-chevron-down' : 'fa-chevron-up'" @click.stop="item.expanded = !item.expanded" />
                </td>
              </tr>

              <TransitionX>
                <tr v-if="expanded && item.expanded" class="border-b border-slate-300 bg-[#e2e8f0]">
                  <td :colspan="localHeaders.length + 1">
                    <slot name="expanded" :item="item"></slot>
                  </td>
                </tr>
              </TransitionX>
            </template>
          </tbody>

          <tfoot>
            <slot name="footer" />
          </tfoot>
        </table>
      </div>
      <div data-tablefull-loading class="relative w-full h-[2px] overflow-hidden bg-primary-200" v-if="loading">
        <div class="absolute inset-y-0 left-0 w-1/3 bg-primary-500 animate-[loading_1s_linear_infinite]"></div>
      </div>
      <div class="table-footer bg-white border-t border-slate-300 p-2">
        <p data-tablefull-empty class="text-center italic py-5" v-if="items && items?.length < 1">Nenhum item a ser exibido.</p>
        <div v-if="!disablePagination">
          <NavigationBar v-if="items?.length" v-model="localControl.page" v-model:limit="localControl.limit" :disablePagination="disablePagination" :totalItems="localControl.total" :totalPages="localControl.pages">
            <template #left>
              <Flex gap-3 center class="text-gray-500">
                <button type="button" class="border-l-2 pl-2 hover:text-primary-600 transition-colors" title="Resetar layout da tabela" @click="resetLayout">
                  <i class="fa-solid fa-rotate-left" />
                </button>
                <button type="button" class="hover:text-primary-600 transition-colors" title="Comprimir colunas" @click="compressLayout">
                  <i class="fa-solid fa-compress" />
                </button>
                <Flex v-if="!disableOrder" gap-3 center class="border-l-2 pl-2">
                  Ordenado por:
                  <div v-for="(order, i) in localControl.order" :key="i" class="bg-slate-200 py-1 px-3 text-xs rounded">
                    {{ order.name }}
                    <i class="fa-solid fa-caret-down cursor-pointer ml-2" v-if="order.direction == 'DESC'" />
                    <i class="fa-solid fa-caret-up cursor-pointer ml-2" v-if="order.direction == 'ASC'" />
                    <i :data-tablefull-order-remove="`${order.table ?? ''}:${order.column}`" class="fa-solid fa-x cursor-pointer text-[0.8em] ml-2" @click="removeOrder(order.column)" />
                  </div>
                </Flex>
              </Flex>
            </template>
          </NavigationBar>
        </div>
      </div>
    </FlexCol>
  </div>

  <div class="text-red-400 text-xs mt-3 flex justify-end" v-if="error">
    <b>{{ error }}</b>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue'

const emit = defineEmits(['update:modelValue', 'update:loading', 'selectedItem', 'rightClick', 'doubleClick', 'headersChange', 'resetLayout'])

const props = defineProps({
  modelValue: Object,
  headers: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
  dense: Boolean,
  ultraDense: Boolean,
  expanded: Boolean,
  loading: Boolean,
  disableOrder: Boolean,
  disablePagination: Boolean,
  offline: Boolean,
  stickyActions: {
    type: Boolean,
    default: false,
  },
  notExpandFieldsWith: {
    type: Array,
    default: () => [],
  },
  startsExpanded: Boolean,
  cellBorder: Boolean,
  error: [String, Boolean],
})
// Local Values
const localControl = ref(props.modelValue)
const selectedItem = ref(null)
const localHeaders = ref([])
const draggedHeaderIndex = ref(null)
const dragOverHeaderIndex = ref(null)
const resizingIndex = ref(null)
const resizeStartX = ref(0)
const resizeStartWidth = ref(0)
const MIN_COLUMN_WIDTH = 40
const ACTION_COLUMN_MAX_WIDTH = 180

const cloneHeaders = (headers = []) =>
  (Array.isArray(headers) ? headers : []).map((header) => ({
    ...header,
  }))

const normalizeWidth = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(Math.round(value), MIN_COLUMN_WIDTH)
  }

  const parsed = Number(String(value).replace('px', '').trim())
  return Number.isFinite(parsed) ? Math.max(Math.round(parsed), MIN_COLUMN_WIDTH) : null
}

const syncLocalHeaders = (headers = []) => {
  localHeaders.value = cloneHeaders(headers).map((header) => {
    return {
      ...header,
      runtimeWidth: normalizeWidth(header.runtimeWidth ?? header.width),
    }
  })
}

const emitHeadersChange = () => {
  emit(
    'headersChange',
    localHeaders.value.map((header) => ({
      ...header,
      width: normalizeWidth(header.runtimeWidth ?? header.width),
    })),
  )
}

const isActionHeader = (header) => {
  const title = String(header?.title ?? '')
    .trim()
    .toLowerCase()
  return header?.field === 'actions' || title === 'ações' || title === 'acoes'
}

const getRowKey = (item, index) => item?.id ?? item?.uuid ?? item?.key ?? index

const canMoveHeader = (header) => header?.movable !== false
const canResizeHeader = (header) => header?.resizable !== false && !isActionHeader(header)
//  Computed
const cellSpacing = computed(() => {
  if (props.ultraDense) {
    return 'py-[2px] px-[5px] text-[0.8em]'
  }
  if (props.dense) {
    return 'px-2 py-1 text-sm'
  }
  return 'px-5 py-2'
})

const processedItems = computed(() => {
  let items = props.items || []

  // Se estiver em modo offline, aplica ordenação e paginação localmente
  if (props.offline && items.length > 0) {
    // Ordenação local
    if (localControl.value.order && localControl.value.order.length > 0) {
      items = [...items] // Clone para não mutar o array original

      items.sort((a, b) => {
        for (const orderRule of localControl.value.order) {
          const column = orderRule.column
          let aVal = a[column]
          let bVal = b[column]

          // Tratamento de valores nulos/undefined
          if (aVal == null && bVal == null) continue
          if (aVal == null) return 1
          if (bVal == null) return -1

          // Comparação de strings (case insensitive)
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            aVal = aVal.toLowerCase()
            bVal = bVal.toLowerCase()
          }

          let comparison = 0
          if (aVal < bVal) comparison = -1
          else if (aVal > bVal) comparison = 1

          if (comparison !== 0) {
            return orderRule.direction === 'DESC' ? -comparison : comparison
          }
        }
        return 0
      })
    }

    // Paginação local
    if (localControl.value.limit && localControl.value.page) {
      const start = (localControl.value.page - 1) * localControl.value.limit
      const end = start + localControl.value.limit

      items = items.slice(start, end)
    }
  }

  // Aplica expansão se necessário
  if (props.startsExpanded) {
    return items.map((item) => ({
      ...item,
      expanded: true,
    }))
  }

  return items
})

const hasCustomWidths = computed(() =>
  localHeaders.value.some((header) => {
    if (!canResizeHeader(header)) return false
    return normalizeWidth(header.runtimeWidth ?? header.width) !== null
  }),
)

const tableStyle = computed(() => {
  if (!hasCustomWidths.value) {
    return {
      width: '100%',
    }
  }

  const totalWidth = localHeaders.value.reduce((sum, header) => {
    const width = normalizeWidth(header.runtimeWidth ?? header.width)
    if (width === null) return sum
    return sum + width
  }, props.expanded ? 40 : 0)

  return {
    width: `${Math.max(totalWidth, 1)}px`,
    minWidth: '100%',
  }
})

const getColumnStyle = (header) => {
  const width = normalizeWidth(header.runtimeWidth ?? header.width)
  if (!width) return null

  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
  }
}

const computedClass = (item) => {
  return item.lineClass
}

const freezeCurrentColumnWidths = (headerRow) => {
  if (!headerRow) return

  const cells = Array.from(headerRow.querySelectorAll('th'))
  localHeaders.value.forEach((header, index) => {
    const cell = cells[index]
    if (!cell) return

    const measuredWidth = Math.round(cell.getBoundingClientRect().width)
    if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) return

    header.runtimeWidth = normalizeWidth(header.runtimeWidth ?? header.width ?? measuredWidth)
    header.width = normalizeWidth(header.runtimeWidth)
  })
}

const startResize = (event, index) => {
  const th = event.currentTarget?.parentElement
  if (!th) return

  freezeCurrentColumnWidths(th.parentElement)
  resizingIndex.value = index
  resizeStartX.value = event.clientX
  resizeStartWidth.value = th.getBoundingClientRect().width
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

const onResizeMove = (event) => {
  if (resizingIndex.value === null) return

  const header = localHeaders.value[resizingIndex.value]
  if (!header) return

  const nextWidth = resizeStartWidth.value + (event.clientX - resizeStartX.value)
  header.runtimeWidth = Math.max(Math.round(nextWidth), MIN_COLUMN_WIDTH)
}

const stopResize = () => {
  if (resizingIndex.value === null) return

  const header = localHeaders.value[resizingIndex.value]
  if (header) {
    header.width = normalizeWidth(header.runtimeWidth ?? header.width)
  }

  resizingIndex.value = null
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
  emitHeadersChange()
}

const handleHeaderDragStart = (index, event) => {
  const header = localHeaders.value[index]
  if (!canMoveHeader(header)) {
    event.preventDefault()
    return
  }

  if (resizingIndex.value !== null) {
    event.preventDefault()
    return
  }

  draggedHeaderIndex.value = index
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }
}

const handleHeaderDragOver = (index, event) => {
  if (draggedHeaderIndex.value === null) return
  if (draggedHeaderIndex.value !== index) {
    dragOverHeaderIndex.value = index
  }
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

const handleHeaderDragLeave = (index) => {
  if (dragOverHeaderIndex.value === index) {
    dragOverHeaderIndex.value = null
  }
}

const handleHeaderDrop = (index) => {
  const targetHeader = localHeaders.value[index]
  if (!canMoveHeader(targetHeader)) {
    draggedHeaderIndex.value = null
    dragOverHeaderIndex.value = null
    return
  }

  if (draggedHeaderIndex.value === null || draggedHeaderIndex.value === index) return

  const nextHeaders = [...localHeaders.value]
  const [draggedHeader] = nextHeaders.splice(draggedHeaderIndex.value, 1)
  nextHeaders.splice(index, 0, draggedHeader)
  localHeaders.value = nextHeaders
  draggedHeaderIndex.value = null
  dragOverHeaderIndex.value = null
  emitHeadersChange()
}

const handleHeaderDragEnd = () => {
  draggedHeaderIndex.value = null
  dragOverHeaderIndex.value = null
}

const resetLayout = () => {
  draggedHeaderIndex.value = null
  dragOverHeaderIndex.value = null
  resizingIndex.value = null
  emit('resetLayout')
}

const compressLayout = () => {
  localHeaders.value = localHeaders.value.map((header) => {
    if (isActionHeader(header)) {
      return {
        ...header,
        runtimeWidth: ACTION_COLUMN_MAX_WIDTH,
        width: ACTION_COLUMN_MAX_WIDTH,
      }
    }

    if (!canResizeHeader(header)) return header

    return {
      ...header,
      runtimeWidth: MIN_COLUMN_WIDTH,
      width: MIN_COLUMN_WIDTH,
    }
  })

  emitHeadersChange()
}
// Senders
const setOrder = (header, value, tableName) => {
  const columnName = header.orderField ?? header.field

  //ex basico: { title: 'Código', field: 'id' },
  //ex com join: { title: 'Depósito', field: 'warehouseName', table: 'warehouse', orderField: 'name' }

  const newOrder = {
    name: header.title,
    table: tableName,
    column: columnName,
    direction: value == 1 ? 'DESC' : 'ASC',
  }

  if (header.raw) {
    newOrder.raw = header.raw
  }

  if (header.replaceOrder && value > 0) {
    localControl.value.order = [newOrder]
    return
  }

  // Lógica unificada para online e offline (permite múltiplas ordenações)
  const index = localControl.value.order.findIndex((el) => {
    if (tableName) {
      return el.column === columnName && el.table === tableName
    }
    return el.column === columnName
  })

  if (index < 0 && value > 0) {
    localControl.value.order = [newOrder]
  } else if (index >= 0 && value > 0) {
    localControl.value.order[index] = newOrder
  } else if (index >= 0 && value == 0) {
    localControl.value.order.splice(index, 1)
  }
}

const getStatus = (header) => {
  const columnName = header.orderField ?? header.field
  const hasTable = !!header.table

  const index = localControl.value.order.findIndex((el) => {
    if (hasTable) {
      return el.column === columnName && el.table === header.table
    }
    return el.column === columnName
  })

  if (index === -1) return 0

  return localControl.value.order[index].direction === 'DESC' ? 1 : 2
}

const removeOrder = (column) => {
  const index = localControl.value.order.findIndex((el) => el.column == column)
  localControl.value.order.splice(index, 1)
}

const sendRightClick = (item) => {
  emit('rightClick', item)
}

const sendDoubleClick = (item) => {
  emit('doubleClick', item)
}

const sendLeftClick = (item) => {
  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const compareItem = (item) => {
  return JSON.stringify(item) == selectedItem.value
}

const getItemValue = (item, header) => {
  const rawValue = item[header.field]
  if (header.format && typeof header.format == 'function') {
    const formattedValue = header.format(rawValue, item)
    return formattedValue
  }
  return rawValue
}

const mapNotExpandedItems = (item) => {
  if (!props.notExpandFieldsWith || props.notExpandFieldsWith?.length === 0) {
    return true
  }

  const conditions = props.notExpandFieldsWith.map((field) => {
    return item[field.field] !== field.value
  })

  return conditions.every(Boolean)
}
/* =========================================================
 * Extra - Navegação por teclado (ArrowUp / ArrowDown)
 * ========================================================= */
const getSelectedIndex = () => {
  if (!selectedItem.value) return -1
  const list = processedItems.value || []
  return list.findIndex((it) => JSON.stringify(it) === selectedItem.value)
}

const selectByIndex = (index) => {
  const list = processedItems.value || []
  if (!list.length) return

  const bounded = Math.max(0, Math.min(index, list.length - 1))
  const item = list[bounded]

  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const moveSelection = (delta) => {
  const list = processedItems.value || []
  if (!list.length) return

  const currentIdx = getSelectedIndex()

  // se ainda não tem seleção, começa pela primeira linha (ou última se subir)
  if (currentIdx === -1) {
    selectByIndex(delta > 0 ? 0 : list.length - 1)
    return
  }

  selectByIndex(currentIdx + delta)
}

const onKeyDown = (e) => {
  // não roubar teclado de input/textarea/editor
  const tag = e.target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveSelection(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveSelection(-1)
  }
}
/* ========================= end adicionado ========================= */
//  Watchers
watch(
  () => localControl.value,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { deep: true },
)

watch(
  [() => props.items, () => localControl.value.limit, () => props.offline],
  ([items, limit, offline]) => {
    if (!offline) return

    const total = Array.isArray(items) ? items.length : 0
    const safeLimit = Number(limit) > 0 ? Number(limit) : 1

    localControl.value.total = total
    localControl.value.pages = Math.ceil(total / safeLimit)
  },
  { deep: true, immediate: true },
)

watch(
  () => props.headers,
  (newHeaders) => {
    syncLocalHeaders(newHeaders)
  },
  { deep: true, immediate: true },
)
//  Life Cicle
onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('mousemove', onResizeMove)
  window.addEventListener('mouseup', stopResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown)
  window.removeEventListener('mousemove', onResizeMove)
  window.removeEventListener('mouseup', stopResize)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
})
</script>

<style>
.table-outer {
  overflow-x: auto;
}

thead {
  position: sticky;
  z-index: 3;
  top: 0;
}

.table-header {
  height: 40px;
}

.table-footer {
  position: sticky;
  z-index: 2;
  bottom: 0;
}

@keyframes loading {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}
</style>
